import React from 'react';
import { getSystemUser } from '@/api/accounts';
import type { AxiosError } from 'axios';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import Grid from '@mui/system/Unstable_Grid/Grid';
import { ArrowLeft as ArrowLeftIcon } from '@phosphor-icons/react/dist/ssr/ArrowLeft';
import { Plus as PlusIcon } from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import type { User } from '@/types/v2/user';
import { paths } from '@/paths';
import { useDialog } from '@/hooks/v2/use-dialog';
import { useUser } from '@/hooks/v2/use-user';
import { PropertyItem } from '@/components/core/v2/property-item';
import { PropertyList } from '@/components/core/v2/property-list';

import { AddCommunityModal } from '../components/add-community-modal';
import { AddUnitModal } from '../components/add-unit-modal';
import { EditUnitModal } from '../components/edit-unit-modal';
import { ArchiveSystemUserCard } from './components/archive-system-user-card';
import { EditAccountRoleModal } from '../components/edit-account-role-modal';
import { EditCommunityRoleModal } from '../components/edit-community-role-modal';
import { RestoreSystemUserCard } from './components/restore-system-user-card';
import { AccountRoleCard } from './components/account-role-card';
import { CommunityCard } from './components/community-card';
import type { SystemUserUnit } from './components/community-card';
import { archiveSystemUser, removeUserFromCommunity, removeUserFromUnit } from '@/api/accounts';
import { isLastCommunity, shouldPromptRemoveFromCommunity, unitsInCommunity } from '../lib/removal-rules';
import { useConfirm } from 'material-ui-confirm';

interface SystemUserApiResponse {
  systemUser: User;
}

export function AccountSystemUserPage(): React.JSX.Element {
  const { userId } = useParams<{ userId: string }>();
  const { account } = useUser();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const communityDialog = useDialog();
  const accountRoleDialog = useDialog();
  const [editCommunityRoleTarget, setEditCommunityRoleTarget] = React.useState<{
    communityId: string;
    communityName: string;
    currentRole: string;
  } | null>(null);
  const [addUnitCommunityId, setAddUnitCommunityId] = React.useState<string | null>(null);
  const [editUnitTarget, setEditUnitTarget] = React.useState<SystemUserUnit | null>(null);

  const { data, isLoading, error, refetch } = useQuery<SystemUserApiResponse>({
    queryKey: ['system-user', userId],
    queryFn: async () => {
      return await getSystemUser(account!.id, userId!);
    },
  });

  const systemUser = data?.systemUser;
  const isArchived = !!systemUser?.deleted_at;

  const getErrorMessage = React.useCallback((err: unknown): string => {
    const axiosError = err as AxiosError<{ message?: string }>;
    return axiosError?.response?.data?.message ?? (err instanceof Error ? err.message : 'An error occurred');
  }, []);

  const handleOnSuccess = React.useCallback(() => {
    toast.success('User updated successfully', {
      style: {
        background: '#107E4A',
        color: '#fff',
        borderColor: '#107E4A',
      },
      description: 'The user has been successfully updated.',
      position: 'top-right',
    });
    refetch();
  }, [refetch]);

  const handleRemoveCommunity = async (community: { id: string | number; name: string }) => {
    const communities = (systemUser?.communities ?? []) as Array<{ id: string | number }>;

    if (isLastCommunity(communities)) {
      try {
        await confirm({
          title: 'Archive user?',
          description: `${community.name} is this user's only community. Removing it would leave them with no access. Archive the user instead?`,
          confirmationText: 'Archive User',
        });
        await archiveSystemUser(account!.id, userId!);
        toast.success('User archived');
        refetch();
      } catch {
        // dismissed
      }
      return;
    }

    const affectedUnits = unitsInCommunity((systemUser?.units ?? []) as SystemUserUnit[], community.id);
    const unitNote = affectedUnits.length
      ? ` This will also remove ${affectedUnits.length} unit assignment(s) in this community.`
      : '';

    try {
      await confirm({
        title: 'Remove Community',
        description: `Are you sure you want to remove this user from ${community.name}?${unitNote}`,
      });
      await removeUserFromCommunity(account!.id, userId!, String(community.id));
      toast.success('User removed from community');
      refetch();
    } catch (error: any) {
      if (error?.response) {
        toast.error(error.response?.data?.message || 'Failed to remove user from community');
      }
    }
  };

  const handleRemoveUnit = async (
    community: { id: string | number; name: string; role?: { name?: string } | null },
    unit: SystemUserUnit
  ) => {
    try {
      const unitLabel = unit.unit_number
        ? `Unit ${unit.unit_number}`
        : (unit as any).address || (unit as any).city || 'this unit';
      await confirm({
        title: 'Remove Unit',
        description: `Remove this user from ${unitLabel}?`,
      });
      await removeUserFromUnit(account!.id, userId!, String(unit.id));
      toast.success('User removed from unit');

      const remaining = unitsInCommunity((systemUser?.units ?? []) as SystemUserUnit[], community.id)
        .filter((u) => String(u.id) !== String(unit.id)).length;

      if (shouldPromptRemoveFromCommunity({ remainingUnitsInCommunity: remaining, communityRole: community.role?.name })) {
        try {
          await confirm({
            title: 'Remove from community too?',
            description: `${unitLabel} was the user's last unit in ${community.name} and they're only a community member. Remove them from the community as well?`,
            confirmationText: 'Remove from community',
            cancellationText: 'Keep as member',
          });
          await removeUserFromCommunity(account!.id, userId!, String(community.id));
          toast.success('User removed from community');
        } catch {
          // keep as member
        }
      }

      refetch();
    } catch (error: any) {
      if (error?.response) {
        toast.error(error.response?.data?.message || 'Failed to remove user from unit');
      }
    }
  };

  return (
    <React.Fragment>
      <Box sx={{ mb: 2 }}>
        <Button
          aria-label="Back to System Users"
          onClick={() => {
            navigate(paths.settings.account.systemUsers.index);
          }}
          startIcon={<ArrowLeftIcon size={24} />}
          variant="text"
        >
          Back to System Users
        </Button>
      </Box>

      {isLoading ? <div>Loading...</div> : null}
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {getErrorMessage(error)}
        </Alert>
      ) : null}

      <Stack spacing={4}>
        <Grid container spacing={4}>
          {data ? (
            <>
              <Grid lg={12} xs={12}>
                <Paper
                  elevation={0}
                  sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}
                >
                  <Box
                    sx={{
                      p: { xs: 2, md: 3 },
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      User Details
                    </Typography>
                  </Box>
                  <PropertyList
                    divider={<Divider />}
                    orientation="vertical"
                    sx={{ '--PropertyItem-padding': '12px 24px' }}
                  >
                    {(
                      [
                        { key: 'ID', value: <Chip label={data.systemUser.id} size="small" variant="soft" /> },
                        { key: 'Name', value: `${data.systemUser.first_name} ${data.systemUser.last_name}` },
                        { key: 'Email', value: data.systemUser.email },
                        { key: 'Phone', value: data.systemUser.phone },
                        { 
                          key: 'Status', 
                          value: data.systemUser.deleted_at ? (
                            <Chip label="Archived" size="small" color="default" variant="outlined" />
                          ) : data.systemUser.email_verified_at ? (
                            <Chip label="Verified" size="small" color="success" />
                          ) : (
                            <Chip label="Pending" size="small" color="warning" />
                          )
                        },
                      ] satisfies { key: string; value: React.ReactNode }[]
                    ).map(
                      (item): React.JSX.Element => (
                        <PropertyItem key={item.key} name={item.key} value={item.value} />
                      )
                    )}
                  </PropertyList>
                </Paper>
              </Grid>

              <Grid lg={12} xs={12}>
                <AccountRoleCard
                  accountUsers={systemUser?.account_users as any}
                  isArchived={isArchived}
                  onEdit={accountRoleDialog.handleOpen}
                />
              </Grid>

              <Grid lg={12} xs={12}>
                <Paper
                  elevation={0}
                  sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}
                >
                  <Box
                    sx={{
                      p: { xs: 2, md: 3 },
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Communities & Units
                    </Typography>
                    {!isArchived && (
                      <Button
                        onClick={communityDialog.handleOpen}
                        size="small"
                        startIcon={<PlusIcon size={18} />}
                        variant="outlined"
                      >
                        Add Community
                      </Button>
                    )}
                  </Box>
                  <Box sx={{ p: 2 }}>
                    {systemUser?.communities && (systemUser.communities as any[]).length > 0 ? (
                      <Stack spacing={2}>
                        {(systemUser.communities as any[]).map((community: any) => (
                          <CommunityCard
                            key={community.id}
                            community={community}
                            units={(systemUser.units ?? []) as SystemUserUnit[]}
                            isArchived={isArchived}
                            onEditRole={() =>
                              setEditCommunityRoleTarget({
                                communityId: String(community.id),
                                communityName: community.name,
                                currentRole: community.role?.name ?? '',
                              })
                            }
                            onRemoveCommunity={() => handleRemoveCommunity(community)}
                            onAddUnit={() => setAddUnitCommunityId(String(community.id))}
                            onEditUnit={(unit) => setEditUnitTarget(unit)}
                            onRemoveUnit={(unit) => handleRemoveUnit(community, unit)}
                          />
                        ))}
                      </Stack>
                    ) : (
                      <Typography color="text.secondary" variant="body2">
                        No communities assigned
                      </Typography>
                    )}
                  </Box>
                </Paper>
              </Grid>

              <Grid lg={12} xs={12}>
                {isArchived ? (
                  <RestoreSystemUserCard
                    accountId={account!.id}
                    userId={userId!}
                    userName={`${systemUser.first_name} ${systemUser.last_name}`.trim() || systemUser.email}
                    onSuccess={() => refetch()}
                  />
                ) : (
                  <ArchiveSystemUserCard
                    accountId={account!.id}
                    userId={userId!}
                    userName={`${systemUser.first_name} ${systemUser.last_name}`.trim() || systemUser.email}
                  />
                )}
              </Grid>
            </>
          ) : null}
        </Grid>
      </Stack>

      {!isArchived && (
        <>
          <AddCommunityModal
            accountId={account?.id!}
            userId={userId!}
            existingCommunityIds={
              ((systemUser?.communities ?? []) as any[]).flatMap((c: any) =>
                [c?.uuid, c?.id != null ? String(c.id) : null].filter(Boolean) as string[]
              )
            }
            onClose={communityDialog.handleClose}
            onSuccess={handleOnSuccess}
            open={communityDialog.open}
          />

          <EditAccountRoleModal
            accountId={account?.id!}
            userId={userId!}
            currentRole={systemUser?.account_users?.[0]?.role?.name || ''}
            onClose={accountRoleDialog.handleClose}
            onSuccess={handleOnSuccess}
            open={accountRoleDialog.open}
          />

          {addUnitCommunityId && (
            <AddUnitModal
              accountId={account?.id!}
              userId={userId!}
              communityId={addUnitCommunityId}
              existingUnitIds={((systemUser?.units ?? []) as any[]).map((u: any) => String(u.id))}
              open={Boolean(addUnitCommunityId)}
              onClose={() => setAddUnitCommunityId(null)}
              onSuccess={() => {
                setAddUnitCommunityId(null);
                handleOnSuccess();
              }}
            />
          )}

          {editUnitTarget && (
            <EditUnitModal
              accountId={account?.id!}
              userId={userId!}
              unitId={String(editUnitTarget.id)}
              unitLabel={`Unit ${editUnitTarget.unit_number ?? ''}`.trim()}
              current={{
                role_name: editUnitTarget.role?.name ?? '',
                user_title_id: (editUnitTarget as any).user_title?.id ? String((editUnitTarget as any).user_title.id) : '',
              }}
              open={Boolean(editUnitTarget)}
              onClose={() => setEditUnitTarget(null)}
              onSuccess={() => {
                setEditUnitTarget(null);
                handleOnSuccess();
              }}
            />
          )}
        </>
      )}

      {editCommunityRoleTarget && (
        <EditCommunityRoleModal
          accountId={account?.id!}
          userId={userId!}
          communityId={editCommunityRoleTarget.communityId}
          communityName={editCommunityRoleTarget.communityName}
          currentRole={editCommunityRoleTarget.currentRole}
          open={Boolean(editCommunityRoleTarget)}
          onClose={() => setEditCommunityRoleTarget(null)}
          onSuccess={() => {
            setEditCommunityRoleTarget(null);
            handleOnSuccess();
          }}
        />
      )}
    </React.Fragment>
  );
}
